import { router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useCreateRedirect } from '@/api/hooks/authQueryHooks';
import { useGetAllShows } from '@/api/hooks/showHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import GiftedPersonForm from '@/components/forms/GiftedPersonForm';
import Text from '@/components/Text';
import GiftShowCard from '@/components/trbShow/GiftShowCard';
import { APPSFLYER_CONFIG, GIFT_DISCOUNT_PERCENTAGE } from '@/constants/config';
import { GiftShowsData, giftShowsResolver } from '@/lib/validations/GiftShowSchema';
import { applyDiscount } from '@/utils/applyDiscount';
import { encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';

const GiftSeries = () => {
  const { data: show } = useGetAllShows();
  const { mutateAsync: createRedirect } = useCreateRedirect();

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount, errors }
  } = useForm<GiftShowsData>({
    defaultValues: { gifts: [] },
    mode: 'onSubmit',
    resolver: giftShowsResolver
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'gifts'
  });

  const onSubmit: SubmitHandler<GiftShowsData> = async (data) => {
    if (show) {
      // Making purchase outside iOS App
      if (Platform.OS === 'ios') {
        createRedirect(undefined, {
          onSuccess: async (redirect) => {
            const url = APPSFLYER_CONFIG.af_web_dp;

            const payload = {
              oneTimeToken: redirect.token,
              routeWithSelectedMethod: '(app)/confirmPurchaseGifts',
              availableMethods: 'BOTH',
              showId: show.id,
              giftFormData: JSON.stringify(data),
              fromIOS: true
            };
            const encrypted = await encryptObject(payload);

            const deepLinkValue = new URLSearchParams({
              deep_link_value: `paymentMethods?payload=${encodeURIComponent(encrypted)}`
            });

            await WebBrowser.openBrowserAsync(`${url}?${deepLinkValue}`);
          },
          onError: (e) => {
            console.error(e);
          }
        });
      } else {
        const payload = {
          routeWithSelectedMethod: '(app)/confirmPurchaseGifts',
          availableMethods: 'BOTH',
          showId: show.id,
          giftFormData: JSON.stringify(data)
        };
        const encrypted = await encryptObject(payload);

        router.navigate({
          pathname: '/paymentMethods',
          params: {
            payload: encodeURIComponent(encrypted)
          }
        });
      }
    }
  };

  return (
    <SafeAreaView edges={['bottom']} style={[styles.flexOne, styles.mainContainer]}>
      <View style={styles.content}>
        <View>
          <Text style={[styles.whiteText, styles.headerSubtitle]}>{i18n.t('giftShows.header_subtitle')}</Text>
          <View style={styles.pricesContainer}>
            <Text fontWeight="600" style={styles.price}>
              {i18n.t('trb_show.normal_price_per_show', { PRICE: show?.price })}
            </Text>
            {show && show.price && (
              <Text fontWeight="900" style={styles.specialPrice}>
                {i18n.t('trb_show.special_price_per_show', {
                  PRICE: applyDiscount(parseFloat(show.price), GIFT_DISCOUNT_PERCENTAGE)
                })}
              </Text>
            )}
          </View>

          <View style={styles.giftedPersonContainer}>
            <GiftedPersonForm
              doneLabel={fields.length === 0 ? i18n.t('mix.submit') : undefined}
              showDoneButton={fields.length < 10}
              phonesOnList={fields.map((field) => field.phone)}
              onDone={(data) => append(data)}
            />
          </View>
        </View>

        <FormWrapper minHeightMinus={450} horizontalPadding={0}>
          {fields.length > 0 && (
            <View style={styles.giftListContainer}>
              {fields.map((field, index) => {
                return (
                  <View key={index}>
                    <GiftShowCard
                      showThumbnail={show?.thumbnail ?? '[]'}
                      showPrice={show?.price ?? '-'}
                      firstName={field.firstName}
                      lastName={field.lastName}
                      phone={field.phone}
                      onDelete={() => remove(index)}
                    />
                    {index !== fields.length - 1 && <Divider style={styles.divider} />}
                  </View>
                );
              })}
              {show?.price !== undefined && (
                <Text style={[styles.whiteText, styles.costSummary]}>
                  {i18n.t('giftShows.total_cost_prefix')}{' '}
                  <Text style={styles.whiteText} fontWeight="900">
                    {fields.length}
                  </Text>{' '}
                  {i18n.t('giftShows.total_cost_middle')}{' '}
                  <Text style={styles.whiteText} fontWeight="900">
                    {`$${(+applyDiscount(parseFloat(show.price), GIFT_DISCOUNT_PERCENTAGE) * fields.length).toFixed(2)}`}
                  </Text>
                </Text>
              )}
            </View>
          )}
        </FormWrapper>
        {errors && <Text style={[styles.errorText, styles.giftsError]}>{errors.gifts?.message}</Text>}
        <BrandedButton
          textStyles={{ color: 'black' }}
          onPress={handleSubmit(onSubmit)}
          disabled={!isValid && submitCount > 0}
          style={styles.button}>
          {i18n.t('giftShows.go_to_checkout')}
        </BrandedButton>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  mainContainer: {
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
    paddingVertical: 10
  },

  content: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  headerSubtitle: {
    justifyContent: 'center',
    textAlign: 'center'
  },

  whiteText: {
    color: 'white'
  },

  giftedPersonContainer: {
    marginVertical: 10,
    width: '100%'
  },

  giftListContainer: {
    flex: 1,
    gap: 10
  },

  divider: {
    width: '100%',
    marginTop: 10,
    backgroundColor: 'white'
  },

  costSummary: {
    alignSelf: 'flex-end'
  },

  giftsError: {
    paddingVertical: 10
  },

  button: {
    width: '100%',
    backgroundColor: 'green',
    marginBottom: 10
  },

  pricesContainer: {
    flexDirection: 'row',
    gap: 20,
    justifyContent: 'center',
    marginVertical: 10
  },

  price: {
    color: 'white',
    textDecorationLine: 'line-through'
  },

  specialPrice: {
    color: 'green'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default GiftSeries;
